import { Tab } from '@mui/material';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader, QueryTabPanel, QueryTabs } from 'components';
import { UserWalletDetailsDialog } from 'features/wallets';
import { useQueryTab } from 'hooks';
import { TranslationNamespace } from 'i18n';

import {
  InternalUserWallets,
  InternalUserWalletDetailsDialog,
} from './InternalUserWallets';
import {
  P2PProviderWallets,
  P2PProviderWalletDialog,
} from './P2PProviderWallets';
import {
  PlatformWallets,
  PlatformWalletDetailsDialog,
} from './PlatformWallets';
import { UserWallets } from './UserWallets';

enum WalletsTabs {
  Platform = 'platform',
  Users = 'users',
  UsersExternal = 'users-external',
  P2PProviders = 'p2p-providers',
}

export const AdminWalletsPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.internal_wallets', // TODO: rename to wallets once old wallets will be removed
  });

  const { tab } = useQueryTab();

  const [createPlatformWalletDialogOpen, setCreatePlatformWalletDialogOpen] =
    useState(false);

  const [
    createP2PProviderWalletDialogOpen,
    setCreateP2PProviderWalletDialogOpen,
  ] = useState(false);

  const [userWalletDialogOpen, setUsermWalletDialogOpen] = useState(false);

  const [
    createInternalUserWalletDialogOpen,
    setCreateInternalUserWalletDialogOpen,
  ] = useState(false);

  const handleCreatePlatformWallet = useCallback(() => {
    setCreatePlatformWalletDialogOpen(true);
  }, []);

  const handleCreateUserWallet = useCallback(() => {
    setUsermWalletDialogOpen(true);
  }, []);

  const handleCreateP2PProviderWalletDialogOpen = useCallback(() => {
    setCreateP2PProviderWalletDialogOpen(true);
  }, []);

  const handleCreateInternalUserWallet = useCallback(() => {
    setCreateInternalUserWalletDialogOpen(true);
  }, []);

  const rightContentButton = useMemo(() => {
    if (tab === WalletsTabs.Platform) {
      return {
        onClick: handleCreatePlatformWallet,
      };
    } else if (tab === WalletsTabs.Users) {
      return {
        onClick: handleCreateInternalUserWallet,
      };
    } else if (tab === WalletsTabs.UsersExternal) {
      return {
        onClick: handleCreateUserWallet,
      };
    } else if (tab === WalletsTabs.P2PProviders) {
      return {
        onClick: handleCreateP2PProviderWalletDialogOpen,
      };
    }
  }, [
    tab,
    handleCreatePlatformWallet,
    handleCreateInternalUserWallet,
    handleCreateUserWallet,
    handleCreateP2PProviderWalletDialogOpen,
  ]);

  return (
    <Fragment>
      <PageHeader
        title={t('title')}
        divider={false}
        rightContentButton={rightContentButton}
      />
      <QueryTabs tabsEnum={WalletsTabs}>
        <Tab value={WalletsTabs.Platform} label={t('tabs.platform')} />
        <Tab value={WalletsTabs.Users} label={t('tabs.users')} />
        <Tab
          value={WalletsTabs.UsersExternal}
          label={t('tabs.users_external')}
        />
        <Tab value={WalletsTabs.P2PProviders} label={t('tabs.p2p_providers')} />
      </QueryTabs>
      <QueryTabPanel value={WalletsTabs.Platform}>
        <PlatformWallets />
      </QueryTabPanel>
      <QueryTabPanel value={WalletsTabs.Users}>
        <InternalUserWallets />
      </QueryTabPanel>
      <QueryTabPanel value={WalletsTabs.UsersExternal}>
        <UserWallets />
      </QueryTabPanel>
      <QueryTabPanel value={WalletsTabs.P2PProviders}>
        <P2PProviderWallets />
      </QueryTabPanel>
      <PlatformWalletDetailsDialog
        open={createPlatformWalletDialogOpen}
        onClose={() => setCreatePlatformWalletDialogOpen(false)}
      />
      <UserWalletDetailsDialog
        open={userWalletDialogOpen}
        onClose={() => setUsermWalletDialogOpen(false)}
      />
      <P2PProviderWalletDialog
        open={createP2PProviderWalletDialogOpen}
        onClose={() => setCreateP2PProviderWalletDialogOpen(false)}
      />
      <InternalUserWalletDetailsDialog
        open={createInternalUserWalletDialogOpen}
        onClose={() => setCreateInternalUserWalletDialogOpen(false)}
      />
    </Fragment>
  );
};
